import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaShoppingCart, 
  FaWineBottle, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaTags,
  FaCheck,
  FaTruck,
  FaShieldAlt,
  FaBoxOpen,
  FaEdit,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaInfoCircle,
  FaWarehouse,
  FaBarcode
} from 'react-icons/fa';
import { listProductDetails } from '../actions/productActions';
import { addToCart } from '../actions/cartActions';
import Loader from '../components/ui/Loader';
import Message from '../components/ui/Message';
import api from '../utils/api';
import UnifiedOutboundModal from '../components/inventory/UnifiedOutboundModal';

const ProductDetailPage = () => {
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('description');
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [showUnifiedOutboundModal, setShowUnifiedOutboundModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  
  const productDetails = useSelector(state => state.productDetails);
  const { loading, error, product } = productDetails;
  
  const cart = useSelector(state => state.cart);
  const { addLoading, success } = cart;
  
  const auth = useSelector(state => state.auth);
  const { user } = auth;
  const [warehouseInventory, setWarehouseInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const fetchWarehouseInventory = async () => {
    setInventoryLoading(true);
    try {
      const response = await api.get(`/inventory/batches?product_id=${id}`);
      
      // 按仓库分组批次数据
      const warehouseGroups = {};
      response.data.forEach(batch => {
        const warehouseId = batch.warehouse_id;
        if (!warehouseGroups[warehouseId]) {
          warehouseGroups[warehouseId] = {
            warehouse_id: warehouseId,
            warehouse_name: batch.warehouse_name,
            batches: [],
            total_units: 0,
            total_value: 0,
            batch_count: 0
          };
        }
        
        warehouseGroups[warehouseId].batches.push(batch);
        warehouseGroups[warehouseId].total_units += batch.remaining_units;
        warehouseGroups[warehouseId].total_value += batch.remaining_units * batch.unit_cost;
        warehouseGroups[warehouseId].batch_count += 1;
      });
      
      setWarehouseInventory(Object.values(warehouseGroups));
    } catch (error) {
      console.error('Error fetching warehouse inventory:', error);
    } finally {
      setInventoryLoading(false);
    }
  };
  useEffect(() => {
    dispatch(listProductDetails(id));
    if (user && user.is_admin) {
      fetchWarehouseInventory();
    }
  }, [dispatch, id, user]);
  
  // Reset success message when changing quantity
  useEffect(() => {
    if (success) {
      setShowSuccessAnimation(true);
      const timer = setTimeout(() => {
        setMessage(null);
        setShowSuccessAnimation(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);
  
  // Format price safely
  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2);
  };
  
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0 && value <= product.stock_quantity) {
      setQuantity(value);
    }
  };
  
  const increaseQuantity = () => {
    if (quantity < product.stock_quantity) {
      setQuantity(quantity + 1);
    }
  };
  
  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };
  
  const addToCartHandler = () => {
    if (quantity > product.stock_quantity) {
      setMessage('选择的数量超过库存量');
      return;
    }
    
    dispatch(addToCart(id, quantity));
    setMessage('商品已成功添加到购物车');
  };
  
  const goToCartHandler = () => {
    navigate('/cart');
  };
  
  // Related products - for demonstration, we'll just show 4 copies of the current product
  const relatedProducts = product ? Array(4).fill(product) : [];
  
  return (
    <div className="product-detail-page">
      <div className="mb-6">
        <Link to="/products" className="inline-flex items-center text-gray-600 hover:text-red-700 transition-colors">
          <FaArrowLeft className="mr-2" />
          <span>返回产品列表</span>
        </Link>
      </div>
      
      {loading ? (
        <div className="py-12">
          <Loader />
        </div>
      ) : error ? (
        <Message variant="error">{error}</Message>
      ) : !product || !product.id ? (
        <Message variant="error">找不到产品</Message>
      ) : (
        <>
          {message && (
            <div className="fixed top-4 right-4 z-50">
              <Message 
                variant="success" 
                onClose={() => setMessage(null)}
                className={`${showSuccessAnimation ? 'animate-fadeInRight' : ''}`}
              >
                <div className="flex items-center">
                  <FaCheck className="mr-2" />
                  {message}
                </div>
              </Message>
            </div>
          )}
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-12">
            <div className="md:flex">
              <div className="md:w-1/2 relative">
                <div className="sticky top-4">
                  <div className="relative group">
                    <img
                      src={product.image_url || '/images/default-wine.jpg'}
                      alt={product.name}
                      className="w-full h-auto"
                    />
                    <div className="absolute top-4 left-4">
                      <div className="bg-red-700 text-white px-3 py-1 rounded-full text-sm font-medium">
                        精选
                      </div>
                    </div>
                    {product.stock_quantity <= 5 && product.stock_quantity > 0 && (
                      <div className="absolute top-4 right-4">
                        <div className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                          仅剩 {product.stock_quantity} 件
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  </div>
                  
                  {/* Image thumbnails would go here in a real implementation */}
                  <div className="flex justify-center mt-4 px-6">
                    <div className="w-16 h-16 border-2 border-red-700 rounded-md overflow-hidden mr-2">
                      <img
                        src={product.image_url || '/images/default-wine.jpg'}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="w-16 h-16 border border-gray-200 rounded-md overflow-hidden opacity-75 hover:opacity-100 transition-opacity cursor-pointer mr-2">
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
                        <FaWineBottle size={20} />
                      </div>
                    </div>
                    <div className="w-16 h-16 border border-gray-200 rounded-md overflow-hidden opacity-75 hover:opacity-100 transition-opacity cursor-pointer">
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
                        <FaWineBottle size={20} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="md:w-1/2 p-8">
                <div className="mb-2 flex items-center">
                  <Link to={`/products?category=${product.category_id}`} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full hover:bg-gray-200 transition-colors">
                    {product.category_name || '五粮液'}
                  </Link>
                </div>
                
                <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
                
                {product.chinese_name && (
                  <h2 className="text-2xl text-gray-700 mb-6">{product.chinese_name}</h2>
                )}
                
                <div className="border-t border-b py-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline">
                      <span className="text-red-700 text-3xl font-bold">¥{formatPrice(product.price)}</span>
                      {/* Example of original price if there was a discount */}
                      <span className="text-gray-500 text-lg line-through ml-2">¥{(parseFloat(product.price) * 1.2).toFixed(2)}</span>
                    </div>
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      节省 ¥{(parseFloat(product.price) * 0.2).toFixed(2)}
                    </div>
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-center mb-4">
                    <div className="mr-6 flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${product.stock_quantity > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className={product.stock_quantity > 0 ? 'text-green-700' : 'text-red-700'}>
                        {product.stock_quantity > 0 ? '有货' : '缺货'}
                      </span>
                    </div>
                    
                    {product.alcohol_percentage && (
                      <div className="flex items-center">
                        <FaWineBottle className="text-gray-400 mr-2" />
                        <span>{product.alcohol_percentage}% 酒精度</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center">
                      <FaTruck className="text-gray-400 mr-2" />
                      <span>48小时内发货</span>
                    </div>
                    <div className="flex items-center">
                      <FaShieldAlt className="text-gray-400 mr-2" />
                      <span>官方直营正品</span>
                    </div>
                  </div>
                  
                  {product.stock_quantity > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center mb-2">
                        <span className="text-gray-700 mr-4">数量</span>
                        <div className="flex items-center border rounded-lg overflow-hidden">
                          <button
                            onClick={decreaseQuantity}
                            className="bg-gray-100 px-4 py-2 hover:bg-gray-200 transition-colors"
                            disabled={quantity <= 1}
                          >
                            -
                          </button>
                          <input
                            type="number"
                            value={quantity}
                            onChange={handleQuantityChange}
                            className="w-16 text-center border-l border-r py-2 focus:outline-none"
                            min="1"
                            max={product.stock_quantity}
                          />
                          <button
                            onClick={increaseQuantity}
                            className="bg-gray-100 px-4 py-2 hover:bg-gray-200 transition-colors"
                            disabled={quantity >= product.stock_quantity}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        库存剩余: {product.stock_quantity} 件
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-4">
                    <button
                      onClick={addToCartHandler}
                      className={`bg-red-700 text-white px-8 py-3 rounded-lg flex items-center justify-center hover:bg-red-800 transition-colors ${product.stock_quantity === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={product.stock_quantity === 0 || addLoading}
                      style={{ minWidth: '180px' }}
                    >
                      {addLoading ? (
                        <Loader size="sm" />
                      ) : (
                        <>
                          <FaShoppingCart className="mr-2" />
                          加入购物车
                        </>
                      )}
                    </button>
                    
                    {success && (
                      <button
                        onClick={goToCartHandler}
                        className="border-2 border-red-700 text-red-700 px-8 py-3 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center"
                        style={{ minWidth: '180px' }}
                      >
                        查看购物车
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">产品信息</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {product.volume && (
                      <div className="flex items-center">
                        <FaWineBottle className="text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm text-gray-500">容量</div>
                          <div>{product.volume}ml</div>
                        </div>
                      </div>
                    )}
                    {product.origin && (
                      <div className="flex items-center">
                        <FaMapMarkerAlt className="text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm text-gray-500">产地</div>
                          <div>{product.origin}</div>
                        </div>
                      </div>
                    )}
                    {product.year && (
                      <div className="flex items-center">
                        <FaCalendarAlt className="text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm text-gray-500">年份</div>
                          <div>{product.year}</div>
                        </div>
                      </div>
                    )}
                    {product.category_name && (
                      <div className="flex items-center">
                        <FaTags className="text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm text-gray-500">类别</div>
                          <div>{product.category_name}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {user && user.is_admin && (
                  <>
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold mb-4">库存信息 (管理员专用)</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <FaBoxOpen className="text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm text-gray-500">当前库存</div>
                            <div className={`font-medium ${product.stock_quantity > 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {product.stock_quantity} 件
                              {product.unit_per_box && ` (${Math.floor(product.stock_quantity / product.unit_per_box)}箱)`}
                            </div>
                          </div>
                        </div>
                        
                        {product.min_stock_level > 0 && (
                          <div className="flex items-center">
                            <FaExclamationTriangle className="text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm text-gray-500">库存下限</div>
                              <div>{product.min_stock_level} 件</div>
                            </div>
                          </div>
                        )}
                        
                        {product.cost_price > 0 && (
                          <div className="flex items-center">
                            <FaMoneyBillWave className="text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm text-gray-500">成本价</div>
                              <div>¥{parseFloat(product.cost_price).toFixed(2)}</div>
                            </div>
                          </div>
                        )}
                        
                        {product.model_spec && (
                          <div className="flex items-center">
                            <FaInfoCircle className="text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm text-gray-500">规格型号</div>
                              <div>{product.model_spec}</div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-4">
                        <Link
                          to={`/admin/product/${product.id}`}
                          className="text-red-700 hover:underline inline-flex items-center"
                        >
                          <FaEdit className="mr-1" />
                          编辑产品
                        </Link>

                        {product.stock_quantity > 0 && (
                          <button
                            onClick={() => setShowUnifiedOutboundModal(true)}
                            className="text-blue-700 hover:underline inline-flex items-center"
                          >
                            <FaBarcode className="mr-1" />
                            扫瓶出库
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <FaWarehouse className="mr-2" />
                        仓库库存分布
                      </h3>
                      
                      {inventoryLoading ? (
                        <div className="flex justify-center py-4">
                          <Loader size="sm" />
                        </div>
                      ) : warehouseInventory.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                          <FaBoxOpen className="text-4xl text-gray-300 mx-auto mb-2" />
                          <p className="text-gray-500">该产品暂无库存记录</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {warehouseInventory.map((warehouse) => (
                            <div key={warehouse.warehouse_id} className="border rounded-lg p-4 bg-gray-50">
                              <div className="flex justify-between items-center mb-3">
                                <div>
                                  <h4 className="font-medium text-gray-900 flex items-center">
                                    <FaWarehouse className="mr-2 text-blue-600" />
                                    {warehouse.warehouse_name}
                                  </h4>
                                  <div className="text-sm text-gray-600 mt-1">
                                    {warehouse.batch_count} 个批次 • 
                                    总计 {warehouse.total_units} 瓶 
                                    ({(warehouse.total_units / (product.unit_per_box || 6)).toFixed(1)} 箱) • 
                                    价值 ¥{warehouse.total_value.toFixed(2)}
                                  </div>
                                </div>
                                <Link
                                  to={`/admin/inventory/warehouse/${warehouse.warehouse_id}`}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  查看仓库
                                </Link>
                              </div>
                              
                              {/* 批次详情 */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {warehouse.batches.map((batch) => (
                                  <div key={batch.id} className="bg-white rounded p-3 border">
                                    <div className="flex justify-between items-start mb-2">
                                      <div>
                                        <span className="text-xs font-medium text-gray-500">
                                          批次 #{batch.id}
                                        </span>
                                        <div className="text-sm font-medium">
                                          {batch.remaining_units}/{batch.total_units} 瓶
                                        </div>
                                      </div>
                                      <span className={`px-2 py-1 rounded-full text-xs ${
                                        batch.status === 'full' ? 'bg-green-100 text-green-800' :
                                        batch.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                      }`}>
                                        {batch.status === 'full' ? '完整' : 
                                        batch.status === 'partial' ? '部分' : '空'}
                                      </span>
                                    </div>
                                    
                                    <div className="text-xs text-gray-600 space-y-1">
                                      <div>成本: ¥{parseFloat(batch.unit_cost).toFixed(2)}/瓶</div>
                                      <div>总值: ¥{(batch.remaining_units * batch.unit_cost).toFixed(2)}</div>
                                      {batch.box_barcode && (
                                        <div>条码: {batch.box_barcode.slice(-8)}</div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>                    
                )}
              </div>
            </div>
            
            <div className="px-8 pb-8">
              <div className="border-t mt-6 pt-6">
                <div className="flex border-b">
                  <button
                    className={`px-6 py-3 font-medium ${activeTab === 'description' ? 'text-red-700 border-b-2 border-red-700' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => setActiveTab('description')}
                  >
                    产品描述
                  </button>
                  <button
                    className={`px-6 py-3 font-medium ${activeTab === 'details' ? 'text-red-700 border-b-2 border-red-700' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => setActiveTab('details')}
                  >
                    规格参数
                  </button>
                </div>
                
                <div className="pt-6">
                  {activeTab === 'description' && (
                    <div className="prose max-w-none">
                      {product.description ? (
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line">{product.description}</p>
                      ) : (
                        <p className="text-gray-500 italic">暂无详细描述</p>
                      )}
                      
                      {/* Example content if description is empty */}
                      {!product.description && (
                        <div className="mt-4">
                          <p className="text-gray-700 leading-relaxed">
                            五粮液是中国著名的浓香型白酒，其以"香气悠久、醇厚丰满、绵甜甘洌、谐调完美、尾净悠长"而闻名于世。五粮液传统酿造技艺已有数百年历史，整个酿造过程严格遵循传统工艺，采用优质高粱、小麦、大米、糯米、玉米五种粮食为原料，经过选料、蒸煮、摊凉、下窖发酵、蒸馏、贮存、勾兑、灌装等工序精心酿制而成。
                          </p>
                          <p className="text-gray-700 leading-relaxed mt-4">
                            本产品口感醇厚，香气丰富，入口绵柔，回味悠长，是宴请亲朋好友、商务礼品的理想选择。
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {activeTab === 'details' && (
                    <div>
                      <table className="w-full text-left">
                        <tbody>
                          <tr className="border-b">
                            <th className="py-3 pr-4 text-gray-600 w-1/4">品牌</th>
                            <td className="py-3">五粮液</td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 pr-4 text-gray-600">香型</th>
                            <td className="py-3">浓香型</td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 pr-4 text-gray-600">酒精度</th>
                            <td className="py-3">{product.alcohol_percentage || 52}%vol</td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 pr-4 text-gray-600">容量</th>
                            <td className="py-3">{product.volume || 500}ml</td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 pr-4 text-gray-600">产地</th>
                            <td className="py-3">{product.origin || '四川宜宾'}</td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 pr-4 text-gray-600">年份</th>
                            <td className="py-3">{product.year || '2023年'}</td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 pr-4 text-gray-600">储存方法</th>
                            <td className="py-3">常温、阴凉、干燥、避光保存</td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 pr-4 text-gray-600">适用场景</th>
                            <td className="py-3">送礼、自饮、收藏</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                  

                </div>
              </div>
            </div>
          </div>
          
          {/* Related Products */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">相关产品</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {relatedProducts.map((product, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <Link to={`/product/${product.id}`}>
                    <div className="h-40 overflow-hidden">
                      <img
                        src={product.image_url || '/images/default-wine.jpg'}
                        alt={product.name}
                        className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </Link>
                  
                  <div className="p-4">
                    <Link to={`/product/${product.id}`}>
                      <h3 className="text-lg font-semibold truncate hover:text-red-700">{product.name}</h3>
                    </Link>
                    
                    {product.chinese_name && (
                      <p className="text-gray-600 text-sm mb-2 truncate">{product.chinese_name}</p>
                    )}
                    
                    <div className="flex justify-between items-center mt-3">
                      <p className="text-red-700 font-bold">¥{formatPrice(product.price)}</p>
                      <button className="text-gray-500 hover:text-red-700">
                        <FaShoppingCart />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* 扫瓶码出库模态框 */}
          {showUnifiedOutboundModal && (
            <UnifiedOutboundModal
              onClose={() => setShowUnifiedOutboundModal(false)}
              onSuccess={() => {
                setShowUnifiedOutboundModal(false);
                // 重新获取产品信息和仓库库存
                dispatch(listProductDetails(id));
                if (user && user.is_admin) {
                  fetchWarehouseInventory();
                }
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ProductDetailPage;